import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api/axios';
import toast from 'react-hot-toast';

const PLACEHOLDER = '/assets/no-photo.svg';

export default function InterestsPage() {
  const [received, setReceived] = useState([]);
  const [sent,     setSent]     = useState([]);
  const [tab,      setTab]      = useState('received');
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [recRes, sentRes] = await Promise.all([
          api.get('/interests/received'),
          api.get('/interests/sent'),
        ]);
        setReceived(recRes.data.interests || []);
        setSent(sentRes.data.interests || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const respond = async (interestId, action) => {
    try {
      await api.put(`/interests/${interestId}/respond`, { action });
      setReceived(prev => prev.map(i => i.id === interestId ? { ...i, status: action } : i));
      toast.success(action === 'accepted' ? '✅ Interest accepted! You can now chat.' : 'Interest declined.');
    } catch (err) {
      toast.error('Action failed');
    }
  };

  const statusBadge = (status) => ({
    pending:  'bg-yellow-100 text-yellow-700',
    accepted: 'bg-green-100 text-green-700',
    declined: 'bg-red-100 text-red-700',
  }[status] || '');

  const list = tab === 'received' ? received : sent;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-heading text-2xl font-bold text-gray-900 mb-6">💌 Interests</h1>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {['received', 'sent'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                tab === t ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {t === 'received' ? `Received (${received.length})` : `Sent (${sent.length})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="card p-4 animate-pulse flex gap-4">
                <div className="w-16 h-16 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-5xl mb-4">💌</p>
            <p className="text-gray-500">No {tab} interests yet.</p>
            {tab === 'sent' && (
              <Link to="/search" className="btn-primary mt-4 inline-block">Browse Profiles</Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {list.map(interest => (
              <div key={interest.id} className="card p-4 flex items-center gap-4">
                <Link to={`/profile/${interest.id}`}>
                  <img src={interest.photo || PLACEHOLDER} alt="" className="w-16 h-16 rounded-full object-cover flex-shrink-0" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/profile/${interest.id}`}>
                    <p className="font-semibold text-gray-900 hover:text-primary transition-colors">
                      {interest.first_name} {interest.last_name}
                    </p>
                  </Link>
                  <p className="text-sm text-gray-500">{interest.age} yrs • {interest.district} • {interest.denomination}</p>
                  {interest.message && (
                    <p className="text-xs text-gray-400 mt-1 italic">"{interest.message}"</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(interest.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${statusBadge(interest.status)}`}>
                    {interest.status}
                  </span>
                  {tab === 'received' && interest.status === 'pending' && (
                    <div className="flex gap-2">
                      <button onClick={() => respond(interest.id, 'accepted')}
                        className="text-xs bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-600 transition-colors">
                        Accept
                      </button>
                      <button onClick={() => respond(interest.id, 'declined')}
                        className="text-xs bg-gray-200 text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-300 transition-colors">
                        Decline
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
